import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, BellRing } from "lucide-react";
import { toast } from "sonner";
import { runServerFn } from "@/lib/server-fn";
import {
  listOnCallRotations, upsertOnCallRotation, deleteOnCallRotation,
  listOnCallShifts, upsertOnCallShift, deleteOnCallShift, getCurrentOnCall,
} from "@/lib/oncall.functions";

export const Route = createFileRoute("/_authenticated/app/admin/oncall")({ component: OnCallPage });

function OnCallPage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const listRot = useServerFn(listOnCallRotations);
  const upsertRot = useServerFn(upsertOnCallRotation);
  const delRot = useServerFn(deleteOnCallRotation);
  const listSh = useServerFn(listOnCallShifts);
  const upsertSh = useServerFn(upsertOnCallShift);
  const delSh = useServerFn(deleteOnCallShift);
  const getNow = useServerFn(getCurrentOnCall);

  const { data: rotData } = useQuery({
    queryKey: ["oncall-rotations", tenantId],
    enabled: !!tenantId,
    queryFn: () => listRot({ data: { tenantId: tenantId! } }),
  });
  const { data: nowData } = useQuery({
    queryKey: ["oncall-current", tenantId],
    enabled: !!tenantId,
    queryFn: () => getNow({ data: { tenantId: tenantId! } }),
    refetchInterval: 60_000,
  });

  const [selectedRot, setSelectedRot] = useState<string | null>(null);
  const { data: shiftData } = useQuery({
    queryKey: ["oncall-shifts", selectedRot],
    enabled: !!selectedRot,
    queryFn: () => listSh({ data: { rotationId: selectedRot! } }),
  });

  const [rotOpen, setRotOpen] = useState(false);
  const [rotName, setRotName] = useState("");
  const [rotAck, setRotAck] = useState("15");
  const [rotEsc, setRotEsc] = useState("30");

  const saveRot = async () => {
    if (!tenantId || !rotName) return toast.error("Name required");
    await runServerFn(() => upsertRot({ data: {
      tenantId, name: rotName, ackWindowMinutes: parseInt(rotAck, 10) || 15,
      escalationMinutes: parseInt(rotEsc, 10) || 30, isActive: true,
    } }), "Save rotation");
    setRotOpen(false); setRotName(""); setRotAck("15"); setRotEsc("30");
    qc.invalidateQueries({ queryKey: ["oncall-rotations"] });
  };

  const [shOpen, setShOpen] = useState(false);
  const [shUser, setShUser] = useState("");
  const [shRole, setShRole] = useState<"primary" | "secondary" | "lead">("primary");
  const [shStart, setShStart] = useState("");
  const [shEnd, setShEnd] = useState("");
  const [shEmail, setShEmail] = useState("");
  const [shPhone, setShPhone] = useState("");

  const saveShift = async () => {
    if (!selectedRot || !tenantId || !shUser || !shStart || !shEnd) return toast.error("All fields required");
    await runServerFn(() => upsertSh({ data: {
      rotationId: selectedRot, tenantId, userId: shUser, role: shRole,
      startsAt: new Date(shStart).toISOString(), endsAt: new Date(shEnd).toISOString(),
      contactEmail: shEmail || null, contactPhone: shPhone || null,
    } }), "Save shift");
    setShOpen(false); setShUser(""); setShStart(""); setShEnd(""); setShEmail(""); setShPhone("");
    qc.invalidateQueries({ queryKey: ["oncall-shifts", selectedRot] });
    qc.invalidateQueries({ queryKey: ["oncall-current"] });
  };

  return (
    <>
      <PageHeader title="On-Call Rotations" description="99.9% SLA backing. Define rotations and shifts; the current on-call surfaces on the status page and in incident escalations." />
      <div className="px-4 sm:px-6 py-4 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><BellRing className="h-4 w-4" /> On call right now</CardTitle></CardHeader>
          <CardContent>
            {(nowData?.onCall ?? []).length === 0 ? (
              <div className="text-sm text-muted-foreground">No active on-call shift.</div>
            ) : (
              <ul className="space-y-2 text-sm">
                {(nowData?.onCall ?? []).map((r: any, i: number) => (
                  <li key={i} className="rounded border p-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{r.role}</Badge>
                      <span className="font-medium">{r.rotation_name}</span>
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">{r.user_id.slice(0, 8)}</div>
                    {r.contact_email && <div className="text-xs">{r.contact_email}</div>}
                    {r.contact_phone && <div className="text-xs">{r.contact_phone}</div>}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Rotations</CardTitle>
            <Dialog open={rotOpen} onOpenChange={setRotOpen}>
              <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" />New rotation</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New rotation</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-2"><Label>Name</Label><Input value={rotName} onChange={(e) => setRotName(e.target.value)} placeholder="Platform primary" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Ack window (min)</Label><Input type="number" value={rotAck} onChange={(e) => setRotAck(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Escalation (min)</Label><Input type="number" value={rotEsc} onChange={(e) => setRotEsc(e.target.value)} /></div>
                  </div>
                </div>
                <DialogFooter><Button onClick={saveRot}>Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent className="p-0">
            {(rotData?.rotations ?? []).length === 0 ? (
              <div className="p-6 text-sm text-center text-muted-foreground">No rotations yet.</div>
            ) : (
              <ul className="divide-y">
                {(rotData?.rotations ?? []).map((r: any) => (
                  <li key={r.id} className={`flex items-center justify-between gap-2 px-4 py-3 cursor-pointer hover:bg-muted/50 ${selectedRot === r.id ? "bg-muted/50" : ""}`} onClick={() => setSelectedRot(r.id)}>
                    <div>
                      <div className="font-medium text-sm">{r.name}</div>
                      <div className="text-xs text-muted-foreground">Ack {r.ack_window_minutes}m · Escalate {r.escalation_minutes}m</div>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={async (e) => {
                      e.stopPropagation();
                      if (!confirm("Delete rotation and all shifts?")) return;
                      if (!tenantId) return;
                      await runServerFn(() => delRot({ data: { id: r.id, tenantId } }), "Delete rotation");
                      qc.invalidateQueries({ queryKey: ["oncall-rotations"] });
                      if (selectedRot === r.id) setSelectedRot(null);
                    }}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {selectedRot && (
          <Card className="lg:col-span-3">
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Shifts</CardTitle>
              <Dialog open={shOpen} onOpenChange={setShOpen}>
                <DialogTrigger asChild><Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-2" />Add shift</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add shift</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div className="space-y-2"><Label>User ID (UUID)</Label><Input value={shUser} onChange={(e) => setShUser(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Role</Label>
                      <Select value={shRole} onValueChange={(v) => setShRole(v as any)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="primary">Primary</SelectItem>
                          <SelectItem value="secondary">Secondary</SelectItem>
                          <SelectItem value="lead">Lead</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Start</Label><Input type="datetime-local" value={shStart} onChange={(e) => setShStart(e.target.value)} /></div>
                      <div className="space-y-2"><Label>End</Label><Input type="datetime-local" value={shEnd} onChange={(e) => setShEnd(e.target.value)} /></div>
                    </div>
                    <div className="space-y-2"><Label>Contact email</Label><Input type="email" value={shEmail} onChange={(e) => setShEmail(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Contact phone</Label><Input value={shPhone} onChange={(e) => setShPhone(e.target.value)} /></div>
                  </div>
                  <DialogFooter><Button onClick={saveShift}>Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              {(shiftData?.shifts ?? []).length === 0 ? (
                <div className="p-6 text-sm text-center text-muted-foreground">No shifts yet.</div>
              ) : (
                <ul className="divide-y">
                  {(shiftData?.shifts ?? []).map((s: any) => (
                    <li key={s.id} className="flex items-center justify-between gap-2 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{s.role}</Badge>
                        <span className="font-mono text-xs">{s.user_id.slice(0, 8)}</span>
                        <span className="text-xs text-muted-foreground">{new Date(s.starts_at).toLocaleString()} → {new Date(s.ends_at).toLocaleString()}</span>
                      </div>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={async () => {
                        if (!tenantId) return;
                        await runServerFn(() => delSh({ data: { id: s.id, tenantId } }), "Delete shift");
                        qc.invalidateQueries({ queryKey: ["oncall-shifts", selectedRot] });
                        qc.invalidateQueries({ queryKey: ["oncall-current"] });
                      }}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}